<template>
  <div class="bg-white rounded-xl shadow-md overflow-hidden mb-4 transform transition hover:shadow-lg">
    <!-- Post header with author info -->
    <div class="px-4 pt-4 pb-2 flex items-center justify-between">
      <div class="flex items-center space-x-3">
        <peer-avatar
          :user="postAuthorAvatar"
          size="sm"
          @click="navigateToProfile(post.authorId)"
          class="cursor-pointer"
        />
        <div>
          <h3 
            @click="navigateToProfile(post.authorId)"
            class="font-medium text-gray-900 cursor-pointer hover:text-blue-600 transition-colors text-left"
          >
            {{ post.authorName || 'User' }}
          </h3>
          <p class="text-xs text-gray-500 text-left">{{ formattedDate }}</p>
        </div>
      </div>
      
      <!-- Post actions menu (for author only) -->
      <div v-if="isAuthor" class="relative" ref="menuContainer">
        <button 
          @click="showMenu = !showMenu" 
          class="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
        >
          <span class="material-symbols-outlined text-gray-600">more_vert</span>
        </button>
        
        <div 
          v-if="showMenu" 
          class="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-10"
        >
          <button 
            @click="deletePost" 
            class="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <span class="material-symbols-outlined text-sm mr-2">delete</span>
            Delete
          </button>
        </div>
      </div>
    </div>
    
    <!-- Post content -->
    <div class="px-4 py-2">
      <p class="text-gray-800 whitespace-pre-line text-left">{{ post.content }}</p>
    </div>
    
    <!-- Post image if available -->
    <div v-if="post.imageUrl" class="mt-2">
      <img 
        :src="post.imageUrl" 
        alt="Post image" 
        class="w-full object-contain max-h-96"
        @click="showFullImage = true"
      />
    </div>
    
    <!-- Post stats (likes, comments) -->
    <div class="px-4 py-2 border-t border-gray-100 text-sm text-gray-500">
      <div class="flex justify-between">
        <div v-if="post.likes && post.likes.length > 0">
          <span class="inline-flex items-center">
            <span class="material-symbols-outlined text-sm text-blue-500 mr-1" style="font-variation-settings: 'FILL' 1;">thumb_up</span>
            {{ post.likes.length }}
          </span>
        </div>
        <div v-if="post.comments && post.comments.length > 0" @click="showComments = !showComments" class="cursor-pointer hover:text-blue-600 transition-colors">
          {{ post.comments.length }} {{ post.comments.length === 1 ? 'comment' : 'comments' }}
        </div>
      </div>
    </div>
    
    <!-- Post actions (like, comment) -->
    <div class="px-4 py-2 border-t border-gray-100 flex">
      <button 
        @click="toggleLike" 
        class="flex-1 flex items-center justify-center py-1.5 rounded-md transition-colors"
        :class="hasLiked ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:bg-gray-50'"
      >
        <span 
          class="material-symbols-outlined mr-1.5"
          :style="hasLiked ? {fontVariationSettings: '\'FILL\' 1'} : {}"
        >
          thumb_up
        </span>
        {{ hasLiked ? 'Liked' : 'Like' }}
      </button>
      
      <button 
        @click="focusCommentInput" 
        class="flex-1 flex items-center justify-center py-1.5 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
      >
        <span class="material-symbols-outlined mr-1.5">chat</span>
        Comment
      </button>
    </div>
    
    <!-- Comments section -->
    <div v-if="showComments || newComment" class="border-t border-gray-100 px-4 py-3 bg-gray-50">
      <!-- Existing comments -->
      <div v-if="post.comments && post.comments.length > 0 && showComments" class="mb-3 space-y-3">
        <div 
          v-for="comment in sortedComments" 
          :key="comment.id" 
          class="flex space-x-2"
        >
          <peer-avatar
            :user="commentAvatar(comment)"
            size="sm"
            @click="navigateToProfile(comment.authorId)"
            class="cursor-pointer"
          />
          <div class="flex-1">
            <div class="bg-white p-2 rounded-lg shadow-sm">
              <div class="flex items-center justify-between">
                <h4 
                  @click="navigateToProfile(comment.authorId)"
                  class="text-sm font-bold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                >
                  {{ comment.authorName || 'User' }}
                </h4>
                <span class="text-xs text-gray-500">{{ formatCommentDate(comment.createdAt) }}</span>
              </div>
              <p class="text-sm text-gray-800 mt-1 text-left">{{ comment.text }}</p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- New comment input -->
      <div class="flex space-x-2">
        <peer-avatar :user="currentUserAvatar" size="sm" />
        <div class="flex-1 relative">
          <input
            v-model="newComment"
            ref="commentInput"
            placeholder="Write a comment..."
            class="w-full border border-gray-300 rounded-full py-2 px-3 pr-12 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            @keyup.enter="submitComment"
          />
          <button 
            @click="submitComment" 
            :disabled="!newComment.trim() || commentLoading"
            class="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-600 hover:text-blue-800 disabled:text-gray-400 transition-colors"
          >
            <span class="material-symbols-outlined" v-if="!commentLoading">send</span>
            <svg v-else class="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Full image modal -->
    <div v-if="showFullImage && post.imageUrl" class="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4">
      <div class="max-w-5xl max-h-screen">
        <img 
          :src="post.imageUrl" 
          alt="Post image" 
          class="max-w-full max-h-[90vh] object-contain"
        />
        <button 
          @click="showFullImage = false" 
          class="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-opacity"
        >
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>
    </div>
    
    <!-- Confirmation dialog for deleting post -->
    <div v-if="showDeleteConfirm" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <h3 class="text-lg font-medium text-gray-900 mb-3">Delete Post</h3>
        <p class="text-gray-600 mb-6">Are you sure you want to delete this post? This action cannot be undone.</p>
        <div class="flex justify-end space-x-3">
          <button 
            @click="showDeleteConfirm = false" 
            class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button 
            @click="confirmDelete" 
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import PeerAvatar from './PeerAvatar.vue';

export default {
  components: {
    PeerAvatar
  },
  props: {
    post: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showMenu: false,
      showComments: false,
      newComment: '',
      commentLoading: false,
      showFullImage: false,
      showDeleteConfirm: false,
      handleClickOutside: null // Store reference for cleanup
    };
  },
  computed: {
    ...mapState(['user', 'profile']),
    
    postAuthorAvatar() {
      return {
        name: this.post.authorName || 'User',
        profilePicture: this.post.authorProfilePic || ''
      };
    },

    currentUserAvatar() {
      return {
        name: this.profile?.name || this.user?.email || 'Student',
        profilePicture: this.profile?.profilePicture || ''
      };
    },
    
    isAuthor() {
      return this.user && this.post.authorId === this.user.uid;
    },
    
    hasLiked() {
      return this.$store.getters.hasLikedPost(this.post.id);
    },
    
    formattedDate() {
      if (!this.post.createdAt) return '';
      
      const date = this.post.createdAt?.toDate?.() || new Date(this.post.createdAt);
      const now = new Date();
      const diffMs = now - date;
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHour = Math.floor(diffMin / 60);
      const diffDay = Math.floor(diffHour / 24);
      
      if (diffSec < 60) return 'Just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      if (diffHour < 24) return `${diffHour}h ago`;
      if (diffDay < 7) return `${diffDay}d ago`;
      
      return date.toLocaleDateString();
    },
    
    sortedComments() {
      if (!this.post.comments) return [];
      
      return [...this.post.comments].sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateA - dateB; // Oldest first
      });
    }
  },
  methods: {
    commentAvatar(comment) {
      return {
        name: comment.authorName || 'User',
        profilePicture: comment.authorProfilePic || ''
      };
    },

    async toggleLike() {
      await this.$store.dispatch('toggleLikePost', this.post.id);
    },
    
    formatCommentDate(date) {
      if (!date) return '';
      
      const commentDate = date?.toDate?.() || new Date(date);
      const now = new Date();
      const diffMs = now - commentDate;
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHour = Math.floor(diffMin / 60);
      const diffDay = Math.floor(diffHour / 24);
      
      if (diffSec < 60) return 'Just now';
      if (diffMin < 60) return `${diffMin}m`;
      if (diffHour < 24) return `${diffHour}h`;
      if (diffDay < 7) return `${diffDay}d`;
      
      return commentDate.toLocaleDateString();
    },
    
    focusCommentInput() {
      this.showComments = true;
      this.$nextTick(() => {
        this.$refs.commentInput?.focus();
      });
    },
    
    async submitComment() {
      if (!this.newComment.trim() || this.commentLoading) return;
      
      this.commentLoading = true;
      try {
        const success = await this.$store.dispatch('addComment', {
          postId: this.post.id,
          text: this.newComment.trim()
        });
        
        if (success) {
          this.newComment = '';
        } else {
          throw new Error('Failed to add comment');
        }
      } catch (error) {
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to add comment',
          type: 'error'
        });
      } finally {
        this.commentLoading = false;
      }
    },
    
    deletePost() {
      this.showMenu = false;
      this.showDeleteConfirm = true;
    },
    
    async confirmDelete() {
      try {
        const success = await this.$store.dispatch('deletePost', this.post.id);
        
        if (success) {
          this.$store.dispatch('showAlert', {
            message: 'Post deleted successfully',
            type: 'success'
          });
        } else {
          throw new Error('Failed to delete post');
        }
      } catch (error) {
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to delete post',
          type: 'error'
        });
      } finally {
        this.showDeleteConfirm = false;
      }
    },
    
    navigateToProfile(userId) {
      if (userId) {
        this.$router.push(`/user/${userId}`);
      }
    }
  },
  mounted() {
    // Close menu when clicking outside
    const handleClickOutside = (event) => {
      if (this.$refs.menuContainer && !this.$refs.menuContainer.contains(event.target)) {
        this.showMenu = false;
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    this.handleClickOutside = handleClickOutside; // Store reference for cleanup
  },
  beforeUnmount() {
    // Clean up event listener
    if (this.handleClickOutside) {
      document.removeEventListener('click', this.handleClickOutside);
    }
  }
};
</script>
